import { HttpClient } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import { ConfigService } from "../services/config.service";
import { ILookupModelString, ITranslations } from "../domain/types";
import { EventBusService, EventsEnum } from "./event-bus.service";
import { LocalStorageService } from "./local-storage.service";
import { errorTranslations } from "./error-translations";

@Injectable({
  providedIn: "root",
})
export class LanguageService {
  public languages = ["en-US", "es-US"];
  private readonly currentLanguage = signal("en-US");
  private readonly currentTranslations = signal<ITranslations>({});

  public get language(): string {
    return this.currentLanguage();
  }

  public set language(language: string) {
    if (language !== this.currentLanguage()) {
      this.currentTranslations.set({});
    }
    this.currentLanguage.set(language);
  }

  public get translations(): ITranslations {
    return this.currentTranslations();
  }

  public set translations(translations: ITranslations) {
    this.currentTranslations.set(translations);
  }

  private _serviceId = "repositoryLookup";

  constructor(
    private configService: ConfigService,
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private eventsService: EventBusService
  ) {
    this.eventsService.on(EventsEnum.LanguageChanged).subscribe((event) => {
      this.getTranslations(event.data);
    });
  }

  getTranslations(language: string): void {
    this.language = language;
    const languageEndpoint =
      this.configService.baseUrl +
      this.configService.apiBaseUrl +
      this.configService.apiServices.language +
      `/Get?lang=${language}`;

    this.http.get<ITranslations>(languageEndpoint).subscribe((translations) => {
      // A slower response from a previous language must not replace the
      // currently selected language's messages.
      if (this.language === language) {
        this.translations = translations;
      }
    }, console.error);
  }

  translate(key: string): string {
    const translated = this.translations[key];
    return translated?.trim()
      ? translated
      : errorTranslations[this.language]?.[key] || "";
  }

  // this code comes from legacy/app/services/repositoryLookup
  // passed to common.activateController by the shell component
  public getLanguages = (): Promise<ILookupModelString[]> => {
    let cacheKey = this._serviceId + "languages";
    let value: ILookupModelString[] = this.localStorageService.get<
      ILookupModelString[]
    >(cacheKey, undefined);

    if (value) {
      return Promise.resolve(value);
    }

    const languagesEndpoint =
      this.configService.baseUrl +
      this.configService.apiBaseUrl +
      this.configService.apiServices.lookup +
      "/Languages";

    return this.http
      .get(languagesEndpoint)
      .toPromise()
      .then((languages: Promise<ILookupModelString[]>) => {
        this.localStorageService.set(cacheKey, languages, {
          TTL: this.configService.cacheLongTTL,
        });

        console.log("languages", languages);

        return languages;
      });
  };
}
